import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";

const AUTH_BG = "https://images.unsplash.com/photo-1528759094033-e86a2379be5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGFic3RyYWN0fGVufDB8fHx8MTc4Mjg3MTAwMHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { t } = useTranslation();
  const { login, verifyOtp, formatApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const redirect = new URLSearchParams(location.search).get("redirect") || "/dashboard";

  // OTP challenge state — populated if the server asks for a 6-digit code
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password, remember);
      if (result && result.otp_required) {
        setOtpChallenge(result.otp_challenge);
        setOtpEmail(result.email_masked || email);
        toast.info(t("loginUi.otpSent"));
        return;
      }
      toast.success(t("loginUi.welcomeBack"));
      navigate(redirect);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setOtpBusy(true);
    try {
      await verifyOtp(otpChallenge, otpCode.trim(), remember);
      toast.success(t("loginUi.deviceVerified"));
      navigate(redirect);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || t("loginUi.invalidCode");
      setError(msg);
      toast.error(msg);
    } finally {
      setOtpBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-4">
            {t("auth.login.eyebrow")}
          </div>
          <h1 className="font-display font-black text-4xl text-slate-900 tracking-tighter leading-tight">
            {t("auth.login.titleLine1")}<br /> {t("auth.login.titleLine2")}
          </h1>
          <p className="mt-3 text-slate-600 text-sm">
            {t("auth.login.subtitle")}
          </p>

          <form onSubmit={handleSubmit} className={`mt-10 space-y-5 ${otpChallenge ? "hidden" : ""}`}>
            <div>
              <Label htmlFor="email" className="text-slate-700">{t("auth.login.email")}</Label>
              <Input
                id="email"
                data-testid={LOGIN.emailInput}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11 bg-white border-slate-300 focus-visible:ring-blue-800"
                placeholder={t("loginUi.emailPlaceholder")}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">{t("auth.login.password")}</Label>
                <Link
                  to="/forgot-password"
                  data-testid={LOGIN.forgotPasswordLink}
                  className="text-xs text-blue-800 hover:underline"
                >
                  {t("auth.login.forgot")}
                </Link>
              </div>
              <PasswordInput
                id="password"
                data-testid={LOGIN.passwordInput}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-11 bg-white border-slate-300 focus-visible:ring-blue-800"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}

            <label
              htmlFor="remember"
              className="flex items-center gap-2.5 cursor-pointer select-none group"
            >
              <input
                id="remember"
                data-testid={LOGIN.rememberCheckbox}
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-800 focus:ring-blue-800 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm text-slate-700 group-hover:text-slate-900">
                {t("loginUi.rememberMe")}
              </span>
            </label>

            <Button
              type="submit"
              data-testid={LOGIN.submitButton}
              disabled={loading}
              className="w-full h-11 bg-blue-800 hover:bg-blue-700 text-white rounded-md"
            >
              {loading ? t("auth.login.submitting") : t("auth.login.submit")}
            </Button>
          </form>

          {/* OTP verification step — replaces the login form when the server
              asks for a code (untrusted device / flagged IP / admin-forced). */}
          {otpChallenge && (
            <form
              data-testid="login-otp-form"
              onSubmit={handleOtpSubmit}
              className="mt-10 space-y-5"
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-amber-800 font-bold">
                {t("loginUi.verificationRequired")}
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight">
                  {t("loginUi.checkYourEmail")}
                </h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {t("loginUi.otpInstructions", { email: otpEmail })}
                </p>
              </div>
              <div>
                <Label htmlFor="otp" className="text-slate-700">{t("loginUi.verificationCode")}</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="login-otp-input"
                  maxLength={6}
                  required
                  className="mt-1 h-12 text-center text-2xl font-mono font-black tracking-[0.4em]"
                  placeholder={t("loginUi.codePlaceholder")}
                />
              </div>
              {error && <div className="text-sm text-rose-600">{error}</div>}
              <Button
                type="submit"
                disabled={otpBusy || otpCode.length !== 6}
                data-testid="login-otp-submit"
                className="w-full h-11 bg-blue-900 hover:bg-blue-800 text-white font-semibold"
              >
                {otpBusy ? t("loginUi.verifying") : t("loginUi.verifyAndSignIn")}
              </Button>
              <button
                type="button"
                onClick={() => { setOtpChallenge(null); setOtpCode(""); setError(""); }}
                className="w-full text-xs text-slate-500 hover:text-slate-800 font-semibold"
              >
                ← Back to sign-in
              </button>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {t("loginUi.trustedDevice")}
              </p>
            </form>
          )}

          {/* Demo panel and plain-text credential list REMOVED for launch —
              live site should never expose one-click sign-in to seeded
              accounts. Test creds live in /app/memory/test_credentials.md */}

          <p className="mt-8 text-sm text-slate-600">
            {t("auth.login.noAccount")}{" "}
            <Link
              to="/register"
              data-testid={LOGIN.registerLink}
              className="text-blue-800 font-semibold hover:underline"
            >
              {t("auth.login.createOne")}
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-blue-900/40" />
        <div className="absolute bottom-16 left-16 right-16 text-white">
          <div className="font-serif-quote text-3xl leading-snug">
            {t("auth.login.quoteText")}
          </div>
          <div className="mt-4 text-sm text-blue-100/80 uppercase tracking-[0.2em]">
            {t("auth.login.quoteAuthor")}
          </div>
        </div>
      </div>
    </div>
  );
}
